"use client";

import React, { useState, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { siteConfig } from "@/config/site";
import { flatNavLinks } from "@/config/dashboard";
import { Icons } from "@/components/shared/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";

const WHEEL_DESTINATION = "/dashboard/wheel";
const GEX_DESTINATION = "/dashboard/gex/gex-ai";

function getMarketingDestination(href: string, isGexSection?: boolean) {
  if (isGexSection || href.startsWith("/dashboard/gex")) {
    return GEX_DESTINATION;
  }
  return WHEEL_DESTINATION;
}

export function MarketingSidebar() {
  const pathname = usePathname();
  const [expanded, setExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(flatNavLinks.filter((item) => item.href === "/dashboard/wheel").map((item) => item.href))
  );

  const showSidebar = useMemo(() => {
    if (!pathname) return false;
    // Conversion landing page keeps a minimal chrome (logo + trial button only).
    if (pathname === "/products/options-journal") return false;
    if (pathname === "/") return true;
    if (pathname.startsWith("/products/")) return true;
    if (pathname.startsWith("/tools/")) return true;
    return false;
  }, [pathname]);

  const navItems = useMemo(() => flatNavLinks, []);

  function toggleSection(sectionHref: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionHref)) next.delete(sectionHref);
      else next.add(sectionHref);
      return next;
    });
  }

  if (!showSidebar) return null;

  return (
    <TooltipProvider delayDuration={100}>
      {/* Desktop: fixed left sidebar */}
      <aside
        className={cn(
          "hidden lg:block fixed left-0 top-0 z-50 h-full transition-all duration-200 ease-out",
          expanded ? "w-[220px]" : "w-[52px]"
        )}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
      >
        <div
          className={cn(
            "h-full border-r border-border/50 bg-background/95 backdrop-blur-xl flex flex-col",
            expanded ? "shadow-xl shadow-black/20" : ""
          )}
        >
          {/* Logo area */}
          <div className="flex h-14 items-center px-3 lg:h-[60px] shrink-0">
            {expanded ? (
              <Link href="/" className="flex items-center space-x-1.5">
                <Image
                  src="/_static/landing/quantwheelicon-transparent.png"
                  alt={siteConfig.name}
                  width={24}
                  height={24}
                  className="h-6 w-6"
                />
                <span className="text-lg font-medium">{siteConfig.name}</span>
              </Link>
            ) : (
              <Link href="/" className="mx-auto">
                <Image
                  src="/_static/landing/quantwheelicon-transparent.png"
                  alt={siteConfig.name}
                  width={24}
                  height={24}
                  className="h-6 w-6"
                />
              </Link>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 flex flex-col px-2 pt-2 overflow-y-auto">
            {navItems.map((section) => (
              <div
                key={section.href}
                className={cn(
                  "mb-2 rounded-lg -mx-0.5 px-0.5 py-1",
                  "bg-gradient-to-b from-slate-500/5 via-transparent to-transparent"
                )}
              >
                <div className="flex flex-col gap-0.5">
                  {(() => {
                    const itemsToRender = [section, ...(section.subItems ?? [])];
                    const showSubItems = expandedSections.has(section.href);
                    return itemsToRender
                      .filter((item, index) => {
                        if (index === 0) return true;
                        if (!expanded) return false;
                        return showSubItems;
                      })
                      .map((item, index) => {
                    const isTopLevel = index === 0;
                    const destination = getMarketingDestination(item.href, section.isGexSection);
                    const isActive = pathname === destination || pathname?.startsWith(destination + "/");
                    const Icon = Icons[item.icon || "arrowRight"];
                    const label = isTopLevel ? section.title : item.title;
                    const itemClass = isTopLevel
                      ? "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm font-medium transition-all duration-300 ease-out"
                      : "flex items-center gap-2.5 rounded-md pl-6 pr-2 py-1.5 text-xs font-medium transition-all duration-300 ease-out";

                    return expanded ? (
                      <div key={`${section.href}-${item.href}-${index}`} className="flex items-center">
                        <Link
                          href={destination}
                          className={cn(
                            itemClass,
                            isTopLevel ? "flex-1" : "",
                            isActive
                              ? "bg-emerald-500/10 text-foreground shadow-[0_0_12px_rgba(16,185,129,0.3)]"
                              : "text-muted-foreground hover:text-foreground hover:bg-emerald-500/5 hover:shadow-[0_0_8px_rgba(16,185,129,0.15)]"
                          )}
                        >
                          <Icon className="size-4 shrink-0" />
                          <span className="truncate">{label}</span>
                        </Link>
                        {isTopLevel && section.subItems && section.subItems.length > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleSection(section.href)}
                            className="ml-1 rounded p-1 text-muted-foreground/70 hover:text-foreground hover:bg-muted/50 transition-colors"
                            aria-label={`Toggle ${section.title} section`}
                          >
                            {showSubItems ? (
                              <ChevronDown className="size-3.5" />
                            ) : (
                              <ChevronRight className="size-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    ) : (
                      <Tooltip key={`${section.href}-${item.href}-${index}`}>
                        <TooltipTrigger asChild>
                          <Link
                            href={destination}
                            className={cn(
                              "flex items-center justify-center rounded-md py-2 transition-all duration-300 ease-out",
                              isActive
                                ? "bg-emerald-500/10 text-foreground shadow-[0_0_12px_rgba(16,185,129,0.3)]"
                                : "text-muted-foreground hover:text-foreground hover:bg-emerald-500/5 hover:shadow-[0_0_8px_rgba(16,185,129,0.15)]"
                            )}
                          >
                            <Icon className="size-4" />
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right">{label}</TooltipContent>
                      </Tooltip>
                    );
                  });
                  })()}
                </div>
              </div>
            ))}
          </nav>

          {/* Bottom CTA */}
          {expanded && (
            <div className="px-3 py-3 border-t border-border/30">
              <Link href="/dashboard">
                <Button size="sm" className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          )}
        </div>
      </aside>
      {/* Spacer so main content is not under the sidebar */}
      <div className="hidden lg:block w-[52px] shrink-0" aria-hidden />
    </TooltipProvider>
  );
}
